import { forwardRef, useCallback, KeyboardEvent, useRef, Ref, RefAttributes, type JSX } from "react";

import { useTheme } from "@mui/material";
import { FieldValues, useController, useWatch } from "react-hook-form";

import { BaseFormSearchFieldProps, FormSearchFieldProps, SubscribedFormSearchFieldProps } from "./types";

import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { mergeRefs } from "@/helpers/refs";
import { useValidators } from "@/hooks/useValidators";
import { Close } from "@/icons/filled";
import { Search } from "@/icons/outlined";

function FormSearchFieldInner<T extends FieldValues>(
  { control, value, rules, name, shouldUnregister, ...rest }: FormSearchFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <SubscribedFormSearchField
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
      value={value}
    />
  ) : (
    <BaseFormSearchField {...rest} ref={ref} defaultValue={value} value={value} />
  );
}

FormSearchFieldInner.displayName = "FormSearchField";

export const FormSearchField = forwardRef(FormSearchFieldInner) as <T extends FieldValues>(
  props: FormSearchFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function SubscribedFormSearchFieldInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormSearchFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultValue || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (value: string) => {
      onChange?.(value);
      field.onChange(value);
    },
    [field, onChange]
  );

  return (
    <BaseFormSearchField
      {...rest}
      value={field.value}
      inputRef={inputRef}
      ref={ref}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-search-field"}
    />
  );
}

const SubscribedFormSearchField = forwardRef(SubscribedFormSearchFieldInner) as <T extends FieldValues>(
  props: SubscribedFormSearchFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseFormSearchField = forwardRef<HTMLInputElement, BaseFormSearchFieldProps>(
  ({ value, defaultValue, onChange, placeholder, components, inputRef }, ref) => {
    const theme = useTheme();

    const internalRef = useRef<HTMLInputElement>(null);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    };

    const handleOnClear = useCallback(() => {
      onChange?.("");
      internalRef.current?.focus();
    }, [onChange]);

    const handleOnKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOnClear();
        }
      },
      [handleOnClear]
    );

    return (
      <FormTextField
        inputRef={mergeRefs(internalRef, inputRef, ref)}
        value={value}
        defaultValue={defaultValue}
        onChange={handleOnChange}
        placeholder={placeholder}
        startAdornment={
          <FormIcon {...components?.icon} color={components?.icon?.color ?? "quiet"}>
            <Search />
          </FormIcon>
        }
        endAdornment={
          value && (
            <FormButton
              variant="ghost"
              size="small"
              startIcon={<Close />}
              iconButton
              onClick={handleOnClear}
              components={{ startIcon: { size: "medium", sx: { color: theme.palette.forms.text.supershy } } }}
            />
          )
        }
        onKeyDown={handleOnKeyDown}
        components={{
          endAdornment: {
            sx: {
              marginRight: 0.5,
            },
          },
        }}
      />
    );
  }
);

BaseFormSearchField.displayName = "BaseFormSearchField";
